import { track } from "@/lib/analytics";
import { useRef, useState } from "react";
import { motion, useAnimationFrame, useInView, useMotionValue, useTransform } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { SectionIntro } from "@/components/site/SectionHead";
import { Reveal } from "@/components/site/motion";
import { DemoCredit, DemoMedia } from "@/components/demos/DemoMedia";
import { DEMOS } from "@/content/demos";
import { DEMO_DURATION } from "@/components/demos/canvasKit";

const Frame = ({ index, paused, setPaused, time, playing, frameRef }) => {
  const demo = DEMOS[index];
  const concept = demo.credit?.kind === "concept";
  return (
    <div ref={frameRef} className="overflow-hidden rounded-2xl border border-white/10 bg-ink" data-testid="demo-frame">
      <div className="flex flex-wrap items-center justify-between gap-x-3 border-b border-white/10 px-3 sm:px-5" data-testid="demo-toolbar">
        <span className="mono text-[10px] tracking-[0.1em] text-fg sm:text-[11px]">DEMO {demo.index} / 0{DEMOS.length}</span>
        {concept && <span className="mono text-[10px] tracking-[0.1em] text-fg-2 sm:text-[11px]">CONCEPT</span>}
        <button type="button" onClick={() => { track(paused ? "demo_play" : "demo_pause"); setPaused((value) => !value); }} aria-label={paused ? "Play demonstration" : "Pause demonstration"} className="flex min-h-11 items-center gap-2 rounded px-2 text-sm text-fg transition-colors hover:text-signal focus-visible:outline focus-visible:outline-2 focus-visible:outline-signal" data-testid="demo-playback">
          {paused ? <Play className="h-3.5 w-3.5" aria-hidden="true" /> : <Pause className="h-3.5 w-3.5" aria-hidden="true" />}
          {paused ? "Play" : "Pause"}
        </button>
      </div>
      <div className="hud-corners relative aspect-[4/3] sm:aspect-video" data-testid="demo-viewport">
        <DemoMedia key={demo.id} media={demo.media} title={demo.title} time={time} paused={!playing} />
      </div>
    </div>
  );
};

const DemoList = ({ index, select, progress }) => (
  <div className="flex flex-col border-t border-white/10">
    {DEMOS.map((d, i) => {
      const on = i === index;
      return (
        <button key={d.id} type="button" onClick={() => select(i)} className={`group relative border-b border-white/10 py-5 text-left transition-colors ${on ? "" : "hover:bg-white/[0.015]"}`} data-testid={`demo-tab-${d.id}`} aria-pressed={on}>
          <div className="flex items-baseline gap-4">
            <span className={`mono text-[12px] ${on ? "text-signal" : "text-fg-3"}`}>{d.index}</span>
            <div className="min-w-0">
              <p className={`text-lg font-[520] leading-snug tracking-[-0.015em] transition-colors ${on ? "text-fg" : "text-fg-2 group-hover:text-fg"}`}>{d.title}</p>
              <div aria-hidden={!on} className={`grid transition-[grid-template-rows,opacity] duration-500 ${on ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <p className="eyebrow mt-2 text-signal/90">{d.capability}</p>
                  <p className="mt-2 text-[14px] leading-relaxed text-fg-2">{d.summary}</p>
                </div>
              </div>
            </div>
          </div>
          {on && (
            <span className="absolute bottom-[-1px] left-0 h-px w-full overflow-hidden">
              <motion.span className="block h-full w-full origin-left bg-signal" style={{ scaleX: progress }} data-testid="demo-progress" />
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export const Demos = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const frameRef = useRef(null);
  const visible = useInView(frameRef, { amount: 0.25 });
  const time = useMotionValue(0);
  const progress = useTransform(time, (seconds) => seconds / DEMO_DURATION);
  const playing = visible && !paused;

  const select = (i) => {
    track(["demo_fleet", "demo_cargo", "demo_maintenance"][i]);
    time.set(0);
    setIndex(i);
  };

  // One clock drives the drawing, progress and slide change. Pause and offscreen
  // time never consume part of the demonstration or skip its ending.
  useAnimationFrame((_, delta) => {
    if (!playing || document.hidden) return;
    const next = time.get() + Math.min(delta, 100) / 1000;
    if (next >= DEMO_DURATION) {
      time.set(0);
      setIndex((current) => (current + 1) % DEMOS.length);
    } else {
      time.set(next);
    }
  });

  return (
    <section id="demonstrations" className="bg-ink-2 py-16 sm:py-24" data-testid="demos-section" data-analytics-section="demonstrations">
      <div className="shell">
        <SectionIntro
          label="Demonstrations"
          title="How the work gets done"
          text="Three examples of work without people onboard. These are concepts we’re developing."
        />
        <Reveal className="mt-14 grid gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Frame index={index} paused={paused} setPaused={setPaused} time={time} playing={playing} frameRef={frameRef} />
            <div className="mt-5"><DemoCredit demo={DEMOS[index]} /></div>
          </div>
          <div className="lg:col-span-4">
            <DemoList index={index} select={select} progress={progress} />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
